import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, cn, toast, type IconName } from "@sagot/ui";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useBuildFormatters } from "../shared/format";
import type { AgentConfig } from "../shared/types";
import { EscalationTab } from "./escalation-tab";
import { GeneralTab } from "./general-tab";
import { GlossaryTab } from "./glossary-tab";
import { GuardrailsTab } from "./guardrails-tab";
import { AgentKnowledgeTab } from "./agent-knowledge-tab";
import { InstructionsTab } from "./instructions-tab";
import { ModelTab } from "./model-tab";
import { RetrievalTab } from "./retrieval-tab";
import { LIMITS, SETTINGS_TABS, configToForm, formToUpdate, type SettingsTab, type SettingsValues } from "./settings-model";
import { DangerTab, LanguagesTab, MemoryTab } from "./simple-tabs";
import { useSettingsMutations } from "./use-agent-settings";
import { zodResolver } from "../../../lib/zod-resolver";

const TAB_ICONS: Readonly<Record<SettingsTab, IconName>> = {
  general: "bot", instructions: "scroll-text", model: "brain", retrieval: "search", knowledge: "book-open", guardrails: "shield-check",
  escalation: "headset", languages: "languages", glossary: "book-open", memory: "lock", danger: "alert-triangle",
};

interface Can {
  edit: boolean;
  publish: boolean;
  readOnly: boolean;
}

interface SettingsFormProps {
  config: AgentConfig;
  tab: SettingsTab;
  timezone: string;
  sourceCount: number;
  can: Can;
  dangerProps: Parameters<typeof DangerTab>[0];
}

function useSettingsSchema() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z.custom<SettingsValues>().superRefine((v, ctx) => {
        if (!v.name.trim()) ctx.addIssue({ code: "custom", path: ["name"], message: t("build.agentSettings.general.nameRequired") });
        if (v.audiences.length === 0) ctx.addIssue({ code: "custom", path: ["audiences"], message: t("build.agentSettings.general.audienceRequired") });
        if (!Number.isInteger(v.max_tokens) || v.max_tokens < LIMITS.maxTokensMin || v.max_tokens > LIMITS.maxTokensMax) {
          ctx.addIssue({ code: "custom", path: ["max_tokens"], message: t("build.agentSettings.model.maxTokensRange") });
        }
        if (v.days.some((d) => d.open && d.opens_at >= d.closes_at)) ctx.addIssue({ code: "custom", path: ["days"], message: t("build.agentSettings.escalation.hoursInvalid") });
        if (!Number.isInteger(v.reminder_minutes) || v.reminder_minutes < LIMITS.reminderMin || v.reminder_minutes > LIMITS.reminderMax) {
          ctx.addIssue({ code: "custom", path: ["reminder_minutes"], message: t("build.agentSettings.escalation.reminderRange", { min: LIMITS.reminderMin, max: LIMITS.reminderMax }) });
        }
        if (v.route_to === "person" && !v.default_assignee_user_id) ctx.addIssue({ code: "custom", path: ["default_assignee_user_id"], message: t("build.agentSettings.escalation.personRequired") });
      }),
    [t],
  );
}

function TabNav({ tab, agentId }: { tab: SettingsTab; agentId: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <nav aria-label={t("build.agentSettings.tabsLabel")} className="lg:sticky lg:top-20 lg:self-start">
      <ul className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
        {SETTINGS_TABS.map((key) => (
          <li key={key} className={key === "danger" ? "lg:mt-3" : undefined}>
            <AppLink
              href={consoleRoutes.agentSettings(summary.slug, agentId, key === "general" ? undefined : key)}
              aria-current={key === tab ? "page" : undefined}
              className={cn(
                "flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm font-semibold",
                key === "danger" ? "text-danger hover:bg-danger-soft aria-[current=page]:bg-danger-soft" : "text-fg-muted hover:bg-sunken hover:text-fg aria-[current=page]:bg-primary-soft aria-[current=page]:text-fg",
              )}
            >
              <Icon name={TAB_ICONS[key]} className="size-4" />
              {t(`build.agentSettings.tabs.${key}`)}
              {key === "retrieval" ? <span className="ml-auto rounded-xs bg-sunken px-1.5 text-[10px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.agentSettings.adv")}</span> : null}
            </AppLink>
          </li>
        ))}
      </ul>
      <div className="mt-4 hidden rounded-md border border-line bg-surface p-3 text-xs text-fg-muted lg:block">
        <div className="flex items-center gap-1.5 font-semibold text-fg"><Icon name="info" className="size-3.5 text-primary-ink" />{t("build.agentSettings.appliesTitle")}</div>
        <p className="mt-1">{t("build.agentSettings.appliesBody")}</p>
        <AppLink href={consoleRoutes.widgetStudio(summary.slug, agentId)} className="mt-1 inline-block font-semibold text-primary-ink hover:underline">{t("build.agentSettings.widgetStudio")}</AppLink>
      </div>
    </nav>
  );
}

function SaveBar({ dirty, saving, savedAt, disabled, onDiscard }: { dirty: boolean; saving: boolean; savedAt: string; disabled: boolean; onDiscard: () => void }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  return (
    <div className="sticky bottom-4 z-20">
      <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3 shadow-pop sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm" role="status" aria-live="polite">
          {dirty ? (
            <>
              <Icon name="alert-circle" className="size-4 text-accent-ink" />
              <span className="font-semibold">{t("build.agentSettings.saveBar.dirty")}</span>
              <span className="hidden text-fg-subtle sm:inline">{t("build.agentSettings.saveBar.dirtyHint")}</span>
            </>
          ) : (
            <>
              <Icon name="check-circle" className="size-4 text-success" />
              <span className="text-fg-muted">{t("build.agentSettings.saveBar.saved", { time: fmt.dateTime(savedAt) })}</span>
            </>
          )}
        </div>
        {disabled ? null : (
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" disabled={!dirty || saving} onClick={onDiscard}>{t("build.agentSettings.saveBar.discard")}</Button>
            <Button type="submit" form="agent-settings-form" disabled={!dirty} loading={saving}>
              <Icon name="check" />
              {t("build.agentSettings.saveBar.save")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Whole-form Agent Settings: vertical tab nav, panels and the sticky unsaved-changes bar. */
export function SettingsForm({ config, tab, timezone, sourceCount, can, dangerProps }: SettingsFormProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { update } = useSettingsMutations(config.id);
  const [baseline, setBaseline] = useState(() => configToForm(config));
  const form = useForm<SettingsValues>({ resolver: zodResolver(useSettingsSchema()), defaultValues: baseline });
  const disabled = !can.edit;
  const dirty = form.formState.isDirty;
  const submit = form.handleSubmit(
    (values) => {
      const body = formToUpdate(values, baseline, timezone);
      if (Object.keys(body).length === 0) {
        form.reset(values);
        return;
      }
      update.mutate(body, {
        onSuccess: (next) => {
          const fresh = configToForm(next);
          setBaseline(fresh);
          form.reset(fresh);
          toast.success(t("build.agentSettings.saveBar.savedToast"));
          if (next.reindex_required) toast.info(t("build.agentSettings.saveBar.reindex"));
        },
        onError: (error) => { toast.error(message(error)); },
      });
    },
    () => { toast.error(t("build.agentSettings.saveBar.invalid")); },
  );
  const panel = {
    general: <GeneralTab config={config} disabled={disabled} canPublish={can.publish} />,
    instructions: <InstructionsTab agentId={config.id} disabled={disabled} />,
    model: <ModelTab disabled={disabled} config={config} />,
    retrieval: <RetrievalTab agentId={config.id} sourceCount={sourceCount} disabled={disabled} graphAvailable={config.graph_available} />,
    knowledge: <AgentKnowledgeTab agentId={config.id} audiences={config.audiences} canLink={can.edit} />,
    guardrails: <GuardrailsTab disabled={disabled} />,
    escalation: <EscalationTab disabled={disabled} timezone={timezone} />,
    languages: <LanguagesTab disabled={disabled} />,
    glossary: <GlossaryTab agentId={config.id} behavior={config.behavior} canEdit={!disabled} />,
    memory: <MemoryTab disabled={disabled} />,
    danger: <DangerTab {...dangerProps} />,
  }[tab];
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
      <TabNav tab={tab} agentId={config.id} />
      <FormProvider {...form}>
        <form id="agent-settings-form" noValidate className="min-w-0 space-y-6" onSubmit={(event) => void submit(event)} aria-label={t(`build.agentSettings.tabs.${tab}`)}>
          {can.readOnly || disabled ? <p className="rounded-md bg-surface-2 p-3 text-sm text-fg-muted">{can.readOnly ? t("build.readOnly") : t(tab === "glossary" ? "glossary.tab.readOnly" : "build.agentSettings.readOnly")}</p> : null}
          <section className="space-y-6" data-testid={`settings-panel-${tab}`}>{panel}</section>
          {tab === "danger" || tab === "knowledge" || tab === "glossary" ? null : <SaveBar dirty={dirty} saving={update.isPending} savedAt={config.updated_at} disabled={disabled} onDiscard={() => { form.reset(baseline); toast.info(t("build.agentSettings.saveBar.discarded")); }} />}
        </form>
      </FormProvider>
    </div>
  );
}
